import React, { useState, useEffect, useRef } from 'react';
import { X, Plus } from 'lucide-react';
import { useToast } from './Toast';
import { leadsAPI, projectsAPI } from '../services/api';

const formatStatusLabel = (status) => {
  if (!status) return 'Unknown';
  return status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ');
};

const LeadFormDrawer = ({ isOpen, onClose, onLeadAdded, projects = [], users = [], userRole, initialView = 'general', dynamicConfig = {} }) => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [localProjects, setLocalProjects] = useState(projects);
  const assignableUsers = users.filter((u) => (u.role === 'sales_agent' || u.role === 'agent') && u.is_active !== false);
  const prevOpenRef = useRef(false);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    location: '',
    budget: '',
    property_type: 'Other',
    intent: '',
    project_id: '',
    notes: '',
    assigned_to_id: '',
    purchaser_name: '',
    customer_name: '',
    cnic_passport_no: '',
    contact_number: '',
    deal_type: '',
  });

  // Sync localProjects when projects prop changes
  useEffect(() => {
    setLocalProjects(projects);
  }, [projects]);

  // Reset form only when the drawer opens (avoid wiping user input when async data loads)
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = isOpen;
    if (isOpen && !wasOpen) {
      setFormData({
        name: '',
        phone: '',
        email: '',
        location: '',
        budget: '',
        property_type: 'Other',
        intent: '',
        project_id: '',
        notes: '',
        assigned_to_id: '',
        purchaser_name: '',
        customer_name: '',
        cnic_passport_no: '',
        contact_number: '',
        deal_type: '',
      });
      setShowNewProject(false);
      setNewProjectName('');
    }
  }, [isOpen, initialView]);

  // If we're on the Eighteen dataset, set the default project id when it becomes available
  useEffect(() => {
    if (!isOpen) return;
    if (initialView !== 'eighteen') return;
    if (formData.project_id) return;
    const eighteenProject = projects.find((p) => String(p?.name || '').toLowerCase().includes('eighteen'));
    if (!eighteenProject?.id) return;
    setFormData((prev) => ({ ...prev, project_id: eighteenProject.id }));
  }, [isOpen, initialView, projects, formData.project_id]);

  const formatPhoneInput = (digits) => {
    const cleaned = String(digits).replace(/\D/g, '');
    let core = cleaned;

    if (core.startsWith('92') && core.length > 2) {
      core = core.substring(2);
    }
    if (core.startsWith('0') && core.length > 1) {
      core = core.substring(1);
    }
    if (core.length > 10) {
      core = core.slice(0, 10);
    }

    if (!core) return '';
    if (core.length <= 3) return `+92 ${core}`;
    if (core.length <= 6) return `+92 ${core.slice(0, 3)} ${core.slice(3)}`;
    return `+92 ${core.slice(0, 3)} ${core.slice(3, 6)} ${core.slice(6)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneInput(e.target.value);
    setFormData({ ...formData, phone: formatted });
  };

  const parseBudgetValue = (value) => {
    if (!value) return null;
    const cleaned = String(value).replace(/[^0-9]/g, '');
    if (!cleaned) return null;
    return parseInt(cleaned, 10);
  };

  const handleBudgetChange = (e) => {
    const cleaned = String(e.target.value).replace(/[^0-9]/g, '');
    const num = cleaned ? parseInt(cleaned, 10) : 0;
    const formatted = num
      ? `PKR ${num.toLocaleString('en-PK')}`
      : '';
    setFormData({ ...formData, budget: formatted });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      showToast('Lead Name is required', 'error');
      return;
    }
    if (!formData.phone) {
      showToast('Phone Number is required', 'error');
      return;
    }

    setLoading(true);
    try {
      if (!formData.name) {
        showToast('Lead Name is required', 'error');
        setLoading(false);
        return;
      }
      if (!formData.phone) {
        showToast('Phone Number is required', 'error');
        setLoading(false);
        return;
      }

      if (formData.email || formData.phone) {
        try {
          const duplicateCheck = await leadsAPI.checkDuplicates({ 
            email: formData.email, 
            phone: formData.phone 
          });
          
          if (duplicateCheck.data.hasDuplicates) {
            const confirmed = window.confirm(
              'A lead with this email or phone already exists. Do you want to create it anyway?'
            );
            if (!confirmed) {
              setLoading(false);
              return;
            }
          }
        } catch (dupError) {
          // Ignore duplicate check errors, proceed with creation
        }
      }

      const payload = {
        name: formData.name,
        email: formData.email || null,
        phone: formData.phone || null,
        location: initialView === 'general' ? (formData.location || null) : null,
        property_type: initialView === 'general' ? formData.property_type : null,
        intent: initialView === 'general' ? (formData.intent || null) : null,
        budget: initialView === 'general' ? parseBudgetValue(formData.budget) : null,
        status: 'new',
        source: initialView === 'meta_ads' ? 'Meta Ads' : (initialView === 'eighteen' ? 'Eighteen' : 'General'),
        assigned_to_id: initialView === 'general' ? (formData.assigned_to_id || null) : null,
        follow_up_date: null,
        notes: formData.notes,
        project_id: formData.project_id || null,
        purchaser_name: null,
        customer_name: null,
        cnic_passport_no: null,
      };

      await leadsAPI.create(payload);
      showToast('Lead created successfully', 'success');
      onLeadAdded();
      onClose();
    } catch (error) {
      console.error('Failed to create lead:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to create lead';
      showToast(errorMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/30 z-[60] transition-opacity backdrop-blur-sm"
        onClick={onClose}
      />
      
      <div 
        className={`fixed inset-y-0 right-0 z-[70] w-full max-w-md bg-white shadow-2xl transform transition-transform duration-300 flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-white">
          <h2 className="text-xl font-semibold text-gray-900">Add New Lead</h2>
          <button 
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 bg-gray-50/30">
          <form id="add-lead-form" onSubmit={handleSubmit} className="space-y-6">
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lead Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm"
                placeholder="Lead Name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Number <span className="text-red-500">*</span></label>
              <input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => handlePhoneChange(e)}
                className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm"
                placeholder="+92 3XX XXX XXXX"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address <span className="text-gray-400 font-normal">(Optional)</span></label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm"
                placeholder="john@example.com"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">Project <span className="text-gray-400 font-normal">(Optional)</span></label>
                {!showNewProject && (
                  <button
                    type="button"
                    onClick={() => setShowNewProject(true)}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                  >
                    <Plus size={14} /> Add New
                  </button>
                )}
              </div>
              {showNewProject ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (!newProjectName.trim()) return;
                        try {
                          const res = await projectsAPI.create({ name: newProjectName.trim() });
                          const newProject = res.data;
                          setLocalProjects((prev) => [...prev, newProject]);
                          setFormData((prev) => ({ ...prev, project_id: newProject.id }));
                          setNewProjectName('');
                          setShowNewProject(false);
                        } catch (err) {
                          console.error('Failed to create project:', err);
                        }
                      }
                      if (e.key === 'Escape') {
                        setShowNewProject(false);
                        setNewProjectName('');
                      }
                    }}
                    className="flex-1 px-4 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm"
                    placeholder="Enter project name and press Enter"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => { setShowNewProject(false); setNewProjectName(''); }}
                    className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <select
                  value={formData.project_id}
                  onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                  className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm"
                >
                  <option value="">None</option>
                  {localProjects.map((project) => (
                    <option key={project.id} value={project.id}>{project.name}</option>
                  ))}
                </select>
              )}
            </div>

            {initialView === 'general' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location <span className="text-gray-400 font-normal">(Optional)</span></label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm"
                    placeholder="City, neighborhood or address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Intent <span className="text-gray-400 font-normal">(Optional)</span></label>
                  <select
                    value={formData.intent}
                    onChange={(e) => {
                      const nextIntent = e.target.value;
                      setFormData({ ...formData, intent: nextIntent });
                    }}
                    className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm"
                  >
                    <option value="">Select intent</option>
                    <option value="Buy">Buy</option>
                    <option value="Rent">Rent</option>
                    <option value="Sell">Sell</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Budget <span className="text-gray-400 font-normal">(Optional)</span></label>
                  <input
                    type="text"
                    value={formData.budget}
                    onChange={handleBudgetChange}
                    className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm"
                    placeholder="e.g. PKR 1,500,000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Property Type <span className="text-gray-400 font-normal">(Optional)</span></label>
                  <select
                    value={formData.property_type}
                    onChange={(e) => setFormData({ ...formData, property_type: e.target.value })}
                    className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm"
                  >
                    <option value="Other">Other</option>
                    <option value="Apartment">Apartment</option>
                    <option value="House">House</option>
                    <option value="Plot">Plot</option>
                    <option value="Commercial">Commercial</option>
                    <option value="Studio">Studio</option>
                    <option value="Penthouse">Penthouse</option>
                  </select>
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes <span className="text-gray-400 font-normal">(Optional)</span></label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows="4"
                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm resize-none"
                placeholder="Any initial details or requirements..."
              />
            </div>

            {initialView === 'general' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Agent <span className="text-gray-400 font-normal">(Optional)</span></label>
                <select
                  value={formData.assigned_to_id}
                  onChange={(e) => setFormData({ ...formData, assigned_to_id: e.target.value })}
                  className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm"
                >
                  <option value="">Unassigned</option>
                  {assignableUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.full_name || user.username} ({user.new_leads_count || 0} New)
                    </option>
                  ))}
                </select>
              </div>
            )}

          </form>
        </div>

        <div className="p-6 border-t border-gray-100 bg-white">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="add-lead-form"
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              ) : (
                <Plus size={18} />
              )}
              {loading ? 'Saving...' : 'Save Lead'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default LeadFormDrawer;
